import React, { useState } from "react";
import { X } from "lucide-react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

const defaultItem = {
  id: "",
  title: "",
  type: "action",
  isCompleted: false,
  metadata: {},
};

const modules = {
  toolbar: [
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link"],
    ["clean"],
  ],
};

const formats = ["bold", "italic", "underline", "list", "bullet", "link"];

const ActionableFeedbackDialog = ({ isOpen, onClose, onSubmit }) => {
  const [feedback, setFeedback] = useState("");
  const [items, setItems] = useState([defaultItem]);

  if (!isOpen) return null;

  const handleAddItem = () => {
    setItems([...items, { ...defaultItem, id: Date.now().toString() }]);
  };

  const handleRemoveItem = (index) => {
    const updated = [...items];
    updated.splice(index, 1);
    setItems(updated);
  };

  const handleChangeItem = (index, field, value) => {
    const updated = [...items];
    updated[index][field] = value;
    setItems(updated);
  };

  const handleSubmit = () => {
    const payload = {
      id: Date.now().toString(),
      feedback, // HTML from Quill
      actionableItems: items.map((item) => ({
        ...item,
        id: item.id || Date.now().toString(),
      })),
      isSystemGenerated: false,
      reaction: "",
      submittedBy: {},
      createdAt: Date.now(),
      updatedAt: Date.now(),
      status: "active",
    };

    onSubmit(payload);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="w-full max-w-2xl bg-primaryLighter rounded-md p-4 flex flex-col gap-4">
        {/* Header */}
        <div className="flex justify-between items-center">
          <h2 className="font-semibold text-sm">Add Feedback</h2>
          <button onClick={onClose}>
            <X size={16} />
          </button>
        </div>

        {/* Quill Editor */}
        <div className="flex flex-col gap-1">
          <label className="text-xs text-gray-600">Feedback</label>
          <div className="bg-white rounded-md">
            <ReactQuill
              theme="snow"
              value={feedback}
              onChange={setFeedback}
              modules={modules}
              formats={formats}
              className="text-sm"
            />
          </div>
        </div>

        {/* Actionable Items */}
        <div className="flex flex-col gap-3">
          <div className="flex justify-between items-center">
            <p className="text-xs font-medium">Actionable Items</p>
            <button onClick={handleAddItem} className="text-xs text-blue-600">
              + Add
            </button>
          </div>

          {items.map((item, index) => (
            <div
              key={index}
              className="bg-white p-3 rounded-md flex flex-col gap-2"
            >
              <input
                type="text"
                placeholder="Title"
                className="text-sm p-1 outline-none"
                value={item.title}
                onChange={(e) =>
                  handleChangeItem(index, "title", e.target.value)
                }
              />

              <select
                className="text-xs p-1 outline-none"
                value={item.type}
                onChange={(e) =>
                  handleChangeItem(index, "type", e.target.value)
                }
              >
                <option value="action">Action</option>
                <option value="info">Info</option>
              </select>

              <div className="flex justify-between items-center">
                <label className="text-xs flex gap-1 items-center">
                  <input
                    type="checkbox"
                    checked={item.isCompleted}
                    onChange={(e) =>
                      handleChangeItem(index, "isCompleted", e.target.checked)
                    }
                  />
                  Completed
                </label>

                <button
                  onClick={() => handleRemoveItem(index)}
                  className="text-xs text-red-500"
                >
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2">
          <button
            onClick={onClose}
            className="text-xs px-3 py-1 bg-gray-200 rounded-md"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="text-xs px-3 py-1 bg-blue-600 text-white rounded-md"
          >
            Submit
          </button>
        </div>
      </div>
    </div>
  );
};

export default ActionableFeedbackDialog;
